/**
 * Daily KPI trend — the account-overview graph Art asked for on 2026-07-21.
 *
 * ---------------------------------------------------------------------------
 * Form: ONE metric at a time, as a single-series line + area wash.
 * ---------------------------------------------------------------------------
 * The measures on this dashboard live on wildly different scales — spend runs
 * to thousands, conversions are single digits, CTR is a fraction. Plotting them
 * together needs either one shared axis (which flattens everything except
 * spend into a line on the floor) or a second y-axis (worse: the alignment of
 * the two scales is arbitrary, so the chart invents a correlation that is not
 * in the data). Neither is honest, so the reader picks the metric instead and
 * the single axis is always correctly scaled and correctly formatted for it.
 *
 * ---------------------------------------------------------------------------
 * Colour: a neutral blue, deliberately NOT a direction-of-change colour.
 * ---------------------------------------------------------------------------
 * Red and green are reserved throughout this dashboard for direction-of-change
 * (good/bad per LOWER_IS_BETTER). A trend line in either would read as a verdict
 * — "spend is green, so spend going up is good" — which is exactly the wrong
 * message on a cost metric. `--chart-series-1` (index.css) carries no such
 * meaning; it is also a distinct mid-blue kept clear of the primary-accent
 * azure so a neutral series never reads as the brand colour. Validated per mode
 * against the real card surfaces:
 *
 *   light #2a78d6 on #ffffff → band PASS, chroma PASS, contrast PASS (>= 3:1)
 *   dark  #3987e5 on #141e1f → band PASS, chroma PASS, contrast PASS (>= 3:1)
 *
 * ---------------------------------------------------------------------------
 * Window: the dashboard's own, never a separate picker.
 * ---------------------------------------------------------------------------
 * The period comes in as a prop from the already-loaded dashboard payload, so
 * the chart is provably describing the same days as the tiles beside it. A
 * chart-local range control would let the two silently disagree.
 */
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { RefreshCw, TriangleAlert } from 'lucide-react';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ReferenceDot,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  getKpiTimeseries,
  type KpiTimeseriesPoint,
} from '@/services/googleAdsKpiDashboardService';
import {
  compactTick,
  formatMetric,
  shortDate,
  type MetricFormat,
} from './kpiFormat';

interface KpiTrendChartProps {
  tenantId: string;
  /** ISO YYYY-MM-DD — the dashboard's current window. */
  periodStart: string;
  periodEnd: string;
  /**
   * The charted metric. Owned by the dashboard, not by this chart: the
   * comparison rows above and this chart's own pills both change it, and the
   * row highlight reads it. One copy, so a row click always sets the chart
   * (an earlier effect keyed on the incoming value never re-ran when the
   * same row was clicked twice) and the highlight cannot drift from it.
   * An unknown key falls back to the first metric.
   */
  metricKey: string;
  onMetricChange: (key: string) => void;
  /** Table instead of chart. Owned by the dashboard so a comparison-row
   *  click can switch back to the chart. */
  showTable: boolean;
  onShowTableChange: (showTable: boolean) => void;
}

// ---------------------------------------------------------------------------
// Metrics
//
// The endpoint carries four raw measures per day (spend, clicks, impressions,
// conversions); every ratio below is derived from them here.
//
// Ordered by Art's stated priority (spend, conversions, CPL, CPA, CPC, CTR,
// budget), minus the two the daily endpoint cannot answer:
//   - Cost / LEAD needs the lead-category conversion breakdown, which is a
//     separate query on the dashboard payload and is not segmented by day.
//   - Budget is an account/campaign setting, not a daily metric — it is not in
//     this series at all.
// Showing either as a derived guess would be worse than omitting it.
// ---------------------------------------------------------------------------

interface TrendMetric {
  key: string;
  label: string;
  /** Short axis/summary noun. */
  format: MetricFormat;
  /** null (not 0) when the day has no denominator — a gap, not a zero. */
  value: (point: KpiTimeseriesPoint) => number | null;
  /** Shown under the picker so the reader knows what the ratio is made of. */
  note?: string;
}

export const TREND_METRICS: TrendMetric[] = [
  {
    key: 'spend_usd',
    label: 'Spend',
    format: 'money',
    value: (p) => p.spend_usd,
  },
  {
    key: 'conversions',
    label: 'Conversions',
    format: 'decimal',
    value: (p) => p.conversions,
  },
  {
    key: 'cost_per_conversion_usd',
    label: 'Cost / conversion',
    format: 'money',
    value: (p) => (p.conversions > 0 ? p.spend_usd / p.conversions : null),
    note: 'Days with no conversions have no cost per conversion — the line breaks rather than plotting $0.',
  },
  {
    key: 'cpc_usd',
    label: 'CPC',
    format: 'money',
    value: (p) => (p.clicks > 0 ? p.spend_usd / p.clicks : null),
    note: 'Days with no clicks have no CPC — the line breaks rather than plotting $0.',
  },
  {
    key: 'ctr',
    label: 'CTR',
    format: 'percent',
    value: (p) => (p.impressions > 0 ? p.clicks / p.impressions : null),
    note: 'Days with no impressions have no CTR — the line breaks rather than plotting 0%.',
  },
  { key: 'clicks', label: 'Clicks', format: 'number', value: (p) => p.clicks },
  {
    key: 'impressions',
    label: 'Impressions',
    format: 'number',
    value: (p) => p.impressions,
  },
];

/** The metric shown until someone picks another (Spend). */
export const DEFAULT_TREND_METRIC_KEY = TREND_METRICS[0].key;

/** Keys this chart can plot. The comparison rows use this to decide which
 *  rows are clickable, instead of keeping their own copy of the list. */
export const TREND_METRIC_KEYS: ReadonlySet<string> = new Set(
  TREND_METRICS.map((m) => m.key),
);

interface ChartRow {
  date: string;
  value: number | null;
}

export function KpiTrendChart({
  tenantId,
  periodStart,
  periodEnd,
  metricKey,
  onMetricChange,
  showTable,
  onShowTableChange,
}: KpiTrendChartProps) {

  const trend = useQuery({
    queryKey: ['kpi-timeseries', tenantId, periodStart, periodEnd],
    queryFn: () => getKpiTimeseries(tenantId, periodStart, periodEnd),
    // Same reasoning as the dashboard query: a cold call is a live Google Ads
    // query, so don't retry it three times, and treat it as fresh for the
    // backend's own cache window.
    retry: 1,
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
  });

  const metric =
    TREND_METRICS.find((m) => m.key === metricKey) ?? TREND_METRICS[0];
  const series = trend.data?.series;

  const rows = useMemo<ChartRow[]>(
    () => (series ?? []).map((p) => ({ date: p.date, value: metric.value(p) })),
    [series, metric],
  );

  const summary = useMemo(() => {
    const present = rows.filter(
      (r): r is ChartRow & { value: number } => r.value != null,
    );
    if (present.length === 0) return null;
    const total = present.reduce((sum, r) => sum + r.value, 0);
    const last = present[present.length - 1];
    return {
      average: total / present.length,
      // A total is only meaningful for additive measures; averaging a ratio's
      // daily values is already a different number from the period ratio, so
      // it is labelled "daily average", never "total".
      last,
      days: present.length,
    };
  }, [rows]);

  // 503 means the monthly-management feature flag is off — a configuration
  // state, not a failure. It gets its own copy so nobody debugs a "broken"
  // chart that is simply switched off.
  const flagOff = isFlagOff(trend.error);
  // Nothing to plot at all (off / failed / no days) — as opposed to "this one
  // metric has no values", where switching metric is the obvious next move and
  // the picker must stay.
  const nothingToPlot =
    trend.isLoading || flagOff || trend.isError || rows.length === 0;
  // Only explain the gaps when there actually are some.
  const gapNote =
    !nothingToPlot && metric.note && rows.some((r) => r.value == null)
      ? metric.note
      : null;

  return (
    <Card>
      <CardHeader className="gap-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <CardTitle>Daily trend</CardTitle>
            <CardDescription>
              {/* The table shows every metric at once, so naming the selected
                  one there would be a lie. */}
              {showTable ? 'Every metric' : `${metric.label} per day`},{' '}
              {shortDate(periodStart)}–{shortDate(periodEnd)} — the same window
              as the tiles above.
            </CardDescription>
          </div>
          {nothingToPlot ? null : (
            <Button
              variant="outline"
              size="sm"
              onClick={() => onShowTableChange(!showTable)}
            >
              {showTable ? 'Show chart' : 'Show table'}
            </Button>
          )}
        </div>

        {/* Metric picker. The reader chooses what is plotted because these
            measures cannot share an axis (see the file header). Hidden when
            there is nothing to plot at all — picking a metric would not change
            a flag-off or a failed fetch — and in the table view, which already
            lists every metric. */}
        {nothingToPlot || showTable ? null : (
          <div
            className="flex flex-wrap gap-1.5"
            role="group"
            aria-label="Choose the metric to plot"
          >
            {TREND_METRICS.map((m) => {
              const selected = m.key === metric.key;
              return (
                <button
                  key={m.key}
                  type="button"
                  aria-pressed={selected}
                  onClick={() => onMetricChange(m.key)}
                  className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs transition-colors ${
                    selected
                      ? 'border-foreground/25 bg-muted font-semibold text-foreground'
                      : 'border-border bg-transparent text-muted-foreground hover:bg-muted/60'
                  }`}
                >
                  {/* The swatch — not coloured text — carries series identity. */}
                  <span
                    className="h-1.5 w-1.5 rounded-full"
                    style={{
                      backgroundColor: selected
                        ? 'var(--chart-series-1)'
                        : 'var(--color-border)',
                    }}
                  />
                  {m.label}
                </button>
              );
            })}
          </div>
        )}
      </CardHeader>

      <CardContent>
        {trend.isLoading ? (
          <TrendSkeleton />
        ) : flagOff ? (
          <TrendNotice
            title="Daily trend is switched off"
            body="GOOGLE_ADS_MONTHLY_MGMT_ENABLED is not switched on here, so the series endpoint is unavailable. Nothing is wrong with the account."
          />
        ) : trend.isError ? (
          <TrendError
            message={extractError(trend.error)}
            isFetching={trend.isFetching}
            onRetry={() => void trend.refetch()}
          />
        ) : rows.length === 0 ? (
          <TrendNotice
            title="No daily data for this window"
            body={`Google Ads returned nothing for ${shortDate(periodStart)}–${shortDate(periodEnd)}. This is also what a failed upstream fetch looks like — the backend returns an empty series rather than an error — so retry if the account was definitely serving.`}
          />
        ) : summary == null ? (
          <TrendNotice
            // Metric labels stay cased as written — lowercasing turned "CTR"
            // into "ctr".
            title={`No ${metric.label} in this window`}
            body={`Every day in ${shortDate(periodStart)}–${shortDate(periodEnd)} is missing the figures this metric is built from. Try another metric — spend and impressions are the ones most likely to be populated.`}
          />
        ) : showTable ? (
          <TrendTable series={series ?? []} />
        ) : (
          <div className={trend.isFetching ? 'opacity-60' : undefined}>
            <TrendPlot
              metric={metric}
              rows={rows}
              summary={summary}
              gapNote={gapNote}
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// The plot
// ---------------------------------------------------------------------------

function TrendPlot({
  metric,
  rows,
  summary,
  gapNote,
}: {
  metric: TrendMetric;
  rows: ChartRow[];
  summary: { average: number; last: ChartRow & { value: number }; days: number };
  gapNote: string | null;
}) {
  return (
    <div>
      {/* Direct readout. Every number the tooltip shows is reachable without
          hovering — here, on the end-point label, and in the table view. */}
      <div className="mb-3 flex flex-wrap items-baseline gap-x-5 gap-y-1 text-xs text-muted-foreground">
        <span>
          Latest{' '}
          <span className="font-semibold text-foreground">
            {formatMetric(summary.last.value, metric.format)}
          </span>{' '}
          on {shortDate(summary.last.date)}
        </span>
        <span className="inline-flex items-baseline gap-1.5">
          {/* Keys the reference line drawn across the plot. */}
          <span
            aria-hidden
            className="inline-block h-px w-3 translate-y-[-3px]"
            style={{
              backgroundColor: 'var(--color-muted-foreground)',
              opacity: 0.55,
            }}
          />
          <span>
            Daily average{' '}
            <span className="font-semibold text-foreground">
              {formatMetric(summary.average, metric.format)}
            </span>{' '}
            over {summary.days} {summary.days === 1 ? 'day' : 'days'}
          </span>
        </span>
      </div>

      {/* A break in the line is data, not a rendering bug — say so, otherwise
          the reader reads the gap as a glitch. */}
      {gapNote ? (
        <p className="mb-3 -mt-1 text-xs text-muted-foreground">{gapNote}</p>
      ) : null}

      <ResponsiveContainer width="100%" height={260}>
        <AreaChart
          data={rows}
          // Right margin leaves room for the end-point value label; bottom
          // leaves room for the x-axis band so the card never grows an inner
          // scrollbar.
          margin={{ top: 12, right: 56, bottom: 4, left: 0 }}
        >
          <CartesianGrid
            vertical={false}
            stroke="var(--color-border)"
            strokeWidth={1}
          />
          <XAxis
            dataKey="date"
            tickFormatter={shortDate}
            tickLine={false}
            axisLine={{ stroke: 'var(--color-border)' }}
            minTickGap={24}
            tick={{ fontSize: 11, fill: 'var(--color-muted-foreground)' }}
          />
          <YAxis
            width={60}
            domain={[0, 'auto']}
            tickFormatter={(v: number) => compactTick(v, metric.format)}
            tickLine={false}
            axisLine={false}
            tick={{ fontSize: 11, fill: 'var(--color-muted-foreground)' }}
          />
          <Tooltip
            // The crosshair finds the X — the reader aims at a date, never at
            // a 2px line.
            cursor={{ stroke: 'var(--color-border)', strokeWidth: 1 }}
            content={<TrendTooltip metric={metric} />}
          />
          {/* Recessive reference: is a given day above or below the window's
              own average? Deliberately NOT labelled in-plot — an in-plot label
              lands on top of either the data line (left) or the end-point
              value (right). It is keyed instead by the matching rule beside
              "Daily average" in the readout above, which is a legend and
              cannot collide with anything. */}
          <ReferenceLine
            y={summary.average}
            stroke="var(--color-muted-foreground)"
            strokeOpacity={0.55}
            strokeWidth={1}
          />
          <Area
            type="linear"
            dataKey="value"
            name={metric.label}
            stroke="var(--chart-series-1)"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="var(--chart-series-1)"
            fillOpacity={0.12}
            dot={false}
            activeDot={{
              r: 4,
              // 2px ring in the card background colour, not a border drawn to separate.
              stroke: 'var(--color-card)',
              strokeWidth: 2,
              fill: 'var(--chart-series-1)',
            }}
            // A missing ratio is a gap, not a zero — never bridge it.
            connectNulls={false}
            isAnimationActive={false}
          />
          {/* One direct label, on the endpoint. Labelling every point would be
              chaos and would go unread. */}
          <ReferenceDot
            x={summary.last.date}
            y={summary.last.value}
            r={4}
            fill="var(--chart-series-1)"
            stroke="var(--color-card)"
            strokeWidth={2}
            isFront
            label={{
              value: formatMetric(summary.last.value, metric.format),
              position: 'top',
              fill: 'var(--color-foreground)',
              fontSize: 11,
              fontWeight: 600,
            }}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

// Recharts hands the content component a loose payload; narrow it here rather
// than trusting its generic types.
interface TrendTooltipProps {
  active?: boolean;
  label?: string | number;
  payload?: { value?: number | null }[];
  metric: TrendMetric;
}

function TrendTooltip({ active, label, payload, metric }: TrendTooltipProps) {
  if (!active || !payload || payload.length === 0) return null;
  const value = payload[0]?.value;
  if (value == null) return null;

  return (
    <div className="rounded-md border border-border bg-card px-2.5 py-2 text-xs shadow-sm">
      <div className="text-muted-foreground">
        {typeof label === 'string' ? shortDate(label) : String(label ?? '')}
      </div>
      <div className="mt-1 flex items-center gap-1.5">
        {/* Line key, not a filled box — at tooltip density a box is
            data-weight ink doing a label's job. */}
        <span
          className="h-0.5 w-3 rounded-full"
          style={{ backgroundColor: 'var(--chart-series-1)' }}
        />
        {/* Value leads, label follows — the reader already knows the series. */}
        <span className="font-semibold tabular-nums text-foreground">
          {formatMetric(value, metric.format)}
        </span>
        <span className="text-muted-foreground">{metric.label}</span>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Table view — the WCAG-clean twin. Every value in the chart is here too, so
// nothing is gated behind a hover.
// ---------------------------------------------------------------------------

function TrendTable({ series }: { series: KpiTimeseriesPoint[] }) {
  return (
    <div className="max-h-[320px] overflow-auto rounded-md border border-border">
      <table className="w-full min-w-[520px] text-xs">
        <thead className="sticky top-0 bg-muted/80 backdrop-blur">
          <tr className="text-left text-muted-foreground">
            <th scope="col" className="px-3 py-2 font-medium">
              Date
            </th>
            {TREND_METRICS.map((m) => (
              <th
                key={m.key}
                scope="col"
                className="px-3 py-2 text-right font-medium"
              >
                {m.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {series.map((point) => (
            <tr key={point.date} className="border-t border-border">
              <th
                scope="row"
                className="px-3 py-1.5 text-left font-normal text-muted-foreground"
              >
                {shortDate(point.date)}
              </th>
              {TREND_METRICS.map((m) => (
                <td
                  key={m.key}
                  className="px-3 py-1.5 text-right tabular-nums"
                >
                  {formatMetric(m.value(point), m.format)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// States
// ---------------------------------------------------------------------------

function TrendSkeleton() {
  return (
    <div
      className="h-[260px] animate-pulse rounded-md border border-border bg-muted/40"
      aria-label="Loading daily trend"
    />
  );
}

/** Neutral, non-alarming: nothing is broken, there is just nothing to draw. */
function TrendNotice({ title, body }: { title: string; body: string }) {
  return (
    <div className="rounded-md border border-dashed border-border p-6 text-sm">
      <div className="font-medium text-foreground">{title}</div>
      <p className="mt-1 max-w-2xl text-xs text-muted-foreground">{body}</p>
    </div>
  );
}

function TrendError({
  message,
  isFetching,
  onRetry,
}: {
  message: string;
  isFetching: boolean;
  onRetry: () => void;
}) {
  return (
    <div className="flex flex-col gap-3 rounded-md border border-destructive/30 bg-destructive/5 p-4 text-sm">
      <div className="flex items-start gap-2 text-destructive">
        <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" />
        <div>
          <p>Could not load the daily trend: {message}</p>
          <p className="mt-1 text-xs text-muted-foreground">
            The tiles above are unaffected — they come from a separate request.
          </p>
        </div>
      </div>
      <div>
        <Button
          variant="outline"
          size="sm"
          onClick={onRetry}
          disabled={isFetching}
        >
          <RefreshCw
            className={`mr-2 h-4 w-4 ${isFetching ? 'animate-spin' : ''}`}
          />
          {isFetching ? 'Retrying…' : 'Retry'}
        </Button>
      </div>
    </div>
  );
}

function isFlagOff(err: unknown): boolean {
  return (
    (err as { response?: { status?: number } } | undefined)?.response?.status ===
    503
  );
}

function extractError(err: unknown): string {
  return (
    (err as { response?: { data?: { detail?: string } } })?.response?.data
      ?.detail ?? String(err)
  );
}
